import React, { useMemo, useState } from 'react';
import { Rnd } from 'react-rnd';
import {
  Box, Typography, Paper, Stack, IconButton, TextField,
  CircularProgress,
} from '@mui/material';
import { ISubscriptions } from '@llws/lift-entity-interfaces';
import { SubscriptionCard } from 'src/components/pages/InvoiceGroups/SubscriptionCard';
import { useDrop } from 'react-dnd';
import CloseIcon from '@mui/icons-material/Close';
import { TLoadedState } from 'src/types/TLoadedState';

interface Props {
  subscriptions: ISubscriptions[];
  position: { x: number, y: number }
  subscriptionLoadState: TLoadedState
  onClose: () => void
  onUnassign: (subscriptionId: number) => void;
}

const UnassignedSubscriptionsPanel: React.FC<Props> = ({
  subscriptions, position, subscriptionLoadState, onClose, onUnassign,
}) => {
  const [{ isOver, canDrop }, dropRef] = useDrop(() => ({
    accept: 'SUBSCRIPTION',
    drop: (item: { subscriptionId: number }) => {
      onUnassign(item.subscriptionId);
    },
    canDrop: () => true,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [onUnassign]);

  const [search, setSearch] = useState('');

  const filteredSubscriptions = useMemo(() => {
    const s = search.trim().toLowerCase();

    if (!s) return subscriptions;

    return subscriptions.filter((sub) => {
      const desc = sub.invoiceDescription?.toLowerCase() ?? '';
      const idStr = sub.id?.toString() ?? '';
      return desc.includes(s) || idStr.includes(s);
    });
  }, [search, subscriptions]);

  return (
    <Rnd
      default={{
        x: 100,
        y: position.y,
        width: "100vh",
        height: 400,
      }}
      minWidth={300}
      minHeight={200}
      style={{ zIndex: 1301, position: "fixed", top: '10%', left: '10%' }}
      bounds="window"
      dragHandleClassName="drag-handle"
    >
      <Paper
        elevation={4}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          p: 2,
          gap: 1,
        }}
      >
        <Stack
          direction="row"
          justifyContent={"space-between"}
          className="drag-handle"
          sx={{ cursor: 'move' }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            Unassigned Subscriptions
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <TextField
          size="small"
          fullWidth
          label="Search by Description or ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1 }}
        />

        {
          subscriptionLoadState === 'loading'
            ? <Stack direction="row" justifyContent="center" alignItems="center" mt={3}>
              <CircularProgress />
            </Stack>
            : <Box
              ref={dropRef}
              flex={1}
              overflow="auto"
              pr={1}
              sx={{
                border: isOver ? '2px dashed green' : 'none',
                transition: 'border 0.2s ease-in-out',
              }}
            >
              {
                subscriptions.length === 0
                  ? <Box display="flex" flexWrap="wrap" gap={1} justifyContent="center" alignItems="center">
                    <Typography>
                      No active subscriptions in this month
                    </Typography>
                  </Box>
                  : <Box display="flex" flexWrap="wrap" gap={1}>
                    {filteredSubscriptions.map((sub) => (
                      <SubscriptionCard key={sub.id} subscription={sub} />
                    ))}
                  </Box>
              }
            </Box>
        }
      </Paper>
    </Rnd>
  );
};

export default UnassignedSubscriptionsPanel;
